// src/pages/SalesList.tsx
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import EmptyState from "../components/EmptyState";
import { StatusBubble } from "../components/StatusBubble";
import { ListToolbar, MenuItem, SortOption } from "../components/ListToolbar";
import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { formatMoney } from "../utils/formatNumber";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { A } from "../styles/appleClasses";
import { Button } from "../components/Button";
import ListTableShell from "../components/lists/ListTableShell";

// Shared sort helpers
import {
  SortKey,
  SortDir,
  compareDates,
  compareInvoiceNo,
  tieBreakByDateThenId,
} from "../utils/sortUtils";

/** Derive types from StatusBubble to avoid “DotColor vs DotColor” nominal mismatch */
type StatusBubbleProps = React.ComponentProps<typeof StatusBubble>;
type BubbleType = StatusBubbleProps extends { bubbleType: infer T } ? T : any;
type DotColor = StatusBubbleProps extends { dotColor?: infer T } ? T : any;

interface Customer {
  id: number | null;
  name: string;
  email: string;
}

interface SaleLine {
  id: number;
  item_kind?: "product" | "service";
  qty?: number;
  fulfilled_qty?: number;
  preorder?: boolean;
  product_id?: number | null;
  product_name?: string | null;
  detail?: string | null;
}

interface PaymentMethodLite {
  id: number;
  name: string;
  provider?: string; // "stripe" | ...
  online?: boolean;
}
const isStripeOnline = (pm?: PaymentMethodLite | null) =>
  !!pm && pm.provider === "stripe" && pm.online === true;

interface Sale {
  id: number;
  date: string;
  customer: Customer | null;
  amount: number;
  status: string | null;
  invoice: string | null;
  on_account: boolean;
  payment_account_id: number | null;
  payment_method?: PaymentMethodLite | null;
  invoice_id: string | number | null;
  lines: SaleLine[];
  voided?: boolean;
  isPreorder?: boolean; // derived locally
  overdue?: boolean;
  fulfilled?: boolean;
  status_raw?: string | null;
  payment_status?: string | null;
  via_paylink?: boolean;
  needs_attention?: boolean;
  gateway_provider?: string | null;
  gateway_method?: string | null;
  paylink_url?: string | null;
  tags?: string[]; // include tags so we can look for "paylink"
}

function formatDate(d: string | null) {
  return formatAnyDateToLocale(d);
}

function formatAmount(amount: number) {
  return amount != null ? `${formatMoney(amount)}` : "—";
}
function invoiceStr(s: Sale): string | null {
  return s.invoice ?? (s.invoice_id != null ? String(s.invoice_id) : null);
}

/* =========================
   Date range filter helpers
   ========================= */
function isoDay(s?: string | null) {
  return (s || "").slice(0, 10); // expects YYYY-MM-DD
}
function inRange(iso: string, from: string, to: string) {
  if (from && iso < from) return false;
  if (to && iso > to) return false;
  return true;
}
function ymdTodayLocal() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/* =========================
   Local “sticky paylink hint”
   ========================= */
const LS_STICKY_KEY = (saleId: number | string) =>
  `rb_paylink_sticky_${saleId}`;
const STICKY_TTL_MS = 1000 * 60 * 60 * 48; // 48h
function hasFreshStickyPaylink(saleId: number | string) {
  try {
    const raw = localStorage.getItem(LS_STICKY_KEY(saleId));
    if (!raw) return false;
    const { at } = JSON.parse(raw);
    return typeof at === "number" && Date.now() - at < STICKY_TTL_MS;
  } catch {
    return false;
  }
}

/* =========================
   Product helpers
   ========================= */
function getProductNamesFromSale(sale: Sale): string[] {
  const names = (sale.lines || [])
    .map((l) => (l.product_name || l.detail || "").trim())
    .filter(Boolean);

  return Array.from(new Set(names));
}
function formatProductSummary(sale: Sale): string {
  const names = getProductNamesFromSale(sale);
  if (names.length === 0) return "—";
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} +${names.length - 3}`;
}

/* =========================
   Status bubble derivation
   ========================= */
type KnownStatus = "unpaid" | "voided" | "fulfilled" | "preorder" | "paid" | "partial" | "on_account";
// Export wording is a fixed contract, never translated or used as semantic identity.
const CSV_STATUS_LABELS: Record<KnownStatus, string> = {
  unpaid: "Unpaid", voided: "Voided", fulfilled: "Fulfilled",
  preorder: "Preorder", paid: "Paid", partial: "Partial", on_account: "on account",
};
function getStatusBubbleProps(sale: Sale): {
  status: KnownStatus | null;
  fallback?: string;
  bubbleType: BubbleType;
  showDot?: boolean;
  dotColor?: DotColor;
} {
  const statusNorm = (sale.status || "").toLowerCase().trim();
  const isZeroTotal = Math.abs(sale.amount || 0) < 0.005;
  // A retained collection link is an indicator, not payment status.
  const isPayLink = sale.via_paylink === true || !!sale.paylink_url;

  const total = (sale.lines || []).reduce((sum, l) => sum + (l.qty || 0), 0);
  const fulfilled = (sale.lines || []).reduce(
    (sum, l) => sum + (l.fulfilled_qty || 0),
    0,
  );
  const isFulfilled = total > 0 && fulfilled >= total;

  if (sale.voided) {
    return { status: "voided", bubbleType: "voided" as BubbleType };
  }

  if (sale.isPreorder) {
    const status = isFulfilled ? "fulfilled" : "preorder";
    let dotColor: DotColor = "red" as DotColor;

    if (statusNorm === "paid") dotColor = "green" as DotColor;
    else if (statusNorm === "partially paid" || statusNorm === "partial") {
      dotColor = "yellow" as DotColor;
    }

    return {
      status,
      bubbleType: "preorder" as BubbleType,
      showDot: true,
      dotColor,
    };
  }

  if (statusNorm === "paid" || isZeroTotal) {
    return { status: "paid", bubbleType: "paid" as BubbleType };
  }

  if (statusNorm === "partially paid" || statusNorm === "partial") {
    return { status: "partial", bubbleType: "partial" as BubbleType };
  }

  if (
    statusNorm === "unpaid" ||
    statusNorm === "paylink" ||
    statusNorm === "pending_payment"
  ) {
    return {
      status: "unpaid",
      bubbleType: "unpaid" as BubbleType,
      showDot: isPayLink,
      dotColor: isPayLink ? "grey" as DotColor : undefined,
    };
  }

  if (statusNorm === "on account") {
    return { status: "on_account", bubbleType: "voided" as BubbleType };
  }

  return { status: null, fallback: statusNorm || "—", bubbleType: "voided" as BubbleType };
}

const ROWS_PER_PAGE = 10;

/* =========================
   Refetch on focus/visibility
   ========================= */
export function useRefetchOnFocus(refetch: () => void) {
  useEffect(() => {
    const onFocus = () => refetch();
    const onVis = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refetch]);
}

/* =========================
   CSV export
   ========================= */
function csvEscape(v: any) {
  const s = String(v ?? "");
  if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function downloadCsv(filename: string, rows: string[][]) {
  const content = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// canonical “Service” pill (no Tailwind blue-*)
const servicePill =
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-none ring-1 " +
  "bg-appleblue/10 text-appleblue ring-appleblue/20";

export default function SalesList() {
  const { t } = useTranslation();
  const displayStatus = (sale: Sale): StatusBubbleProps => {
    const { status, fallback, ...appearance } = getStatusBubbleProps(sale);
    const labels: Record<KnownStatus, string> = {
      unpaid: t("sales.list.unpaid"), voided: t("sales.list.voided"),
      fulfilled: t("sales.list.fulfilled"), preorder: t("sales.list.preorder"),
      paid: t("sales.list.paid"), partial: t("sales.list.partial"),
      on_account: t("sales.list.onAccount"),
    };
    return { ...appearance, label: status === null ? fallback! : labels[status] };
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();

  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"loadSales" | null>(null);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState(""); // YYYY-MM-DD
  const [page, setPage] = useState(1);

  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const fetchSales = useCallback(async () => {
    if (!company_id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<Sale[]>(
        `/sales?company_id=${company_id}&_=${Date.now()}`,
      );
      setSales(data);
    } catch {
      setError("loadSales");
    } finally {
      setLoading(false);
    }
  }, [company_id]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales, location.key, location.search]);

  useRefetchOnFocus(fetchSales);

  useEffect(() => {
    setPage(1);
  }, [search, fromDate, toDate, sales.length, sortKey, sortDir]);

  const clearFilters = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
  };

  const filteredSales = useMemo(() => {
    const q = search.toLowerCase().trim();

    return sales.filter((sale) => {
      const day = isoDay(sale.date);
      if (!inRange(day, fromDate, toDate)) return false;

      if (!q) return true;

      const productsStr = getProductNamesFromSale(sale)
        .join(", ")
        .toLowerCase();
      const disp = formatDate(sale.date).toLowerCase(); // allow searching MM/DD/YYYY too

      return (
        (sale.invoice || "").toLowerCase().includes(q) ||
        String(sale.invoice_id || "")
          .toLowerCase()
          .includes(q) ||
        (sale.customer?.name || "").toLowerCase().includes(q) ||
        (sale.status || "").toLowerCase().includes(q) ||
        (sale.date || "").toLowerCase().includes(q) ||
        disp.includes(q) ||
        String(sale.amount).includes(q) ||
        productsStr.includes(q)
      );
    });
  }, [sales, search, fromDate, toDate]);

  const sortedSales = useMemo(() => {
    const list = filteredSales.slice();
    list.sort((a, b) => {
      if (sortKey === "date") {
        const cmp = compareDates(a.date, b.date, sortDir);
        if (cmp !== 0) return cmp;
        const inv = compareInvoiceNo(invoiceStr(a), invoiceStr(b), "desc");
        if (inv !== 0) return inv;
        return tieBreakByDateThenId(a.date, b.date, a.id, b.id);
      } else {
        const cmp = compareInvoiceNo(invoiceStr(a), invoiceStr(b), sortDir);
        if (cmp !== 0) return cmp;
        return tieBreakByDateThenId(a.date, b.date, a.id, b.id);
      }
    });
    return list;
  }, [filteredSales, sortKey, sortDir]);

  const totalFiltered = sortedSales.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedSales = sortedSales.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );
  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  const handleHeaderSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const doExport = () => {
    const rows: string[][] = [
      [
        "Date",
        "Invoice",
        "Customer",
        "Products",
        "Amount",
        "Status",
        "On Account",
      ],
      ...sortedSales.map((s) => {
        const hasPreorder = s.lines?.some((l) => l.preorder);
        const bubble = getStatusBubbleProps({
          ...s,
          isPreorder: !!hasPreorder,
        });
        return [
          isoDay(s.date),
          s.invoice || (s.invoice_id != null ? String(s.invoice_id) : ""),
          s.customer?.name || "",
          formatProductSummary(s),
          String(s.amount ?? ""),
          bubble.status === null ? bubble.fallback! : CSV_STATUS_LABELS[bubble.status],
          s.on_account ? "Yes" : "No",
        ];
      }),
    ];
    downloadCsv(`sales_${ymdTodayLocal()}.csv`, rows);
  };

  // ✅ ListToolbar wiring
  const sortValue = `${sortKey}_${sortDir}`;
  const sortOptions: SortOption[] = [
    { label: t("sales.list.dateNewest"), value: "date_desc" },
    { label: t("sales.list.dateOldest"), value: "date_asc" },
    { label: t("sales.list.invoiceHigh"), value: "invoice_desc" },
    { label: t("sales.list.invoiceLow"), value: "invoice_asc" },
  ];
  const onToolbarSortChange = (v: string) => {
    const [k, d] = v.split("_") as [SortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  const newItems: MenuItem[] = [
    {
      label: t("sales.common.newSale"),
      onClick: () => navigate(companyRoute(company_id!, "/sales/new")),
    },
  ];

  const moreItems: MenuItem[] = [
    { label: t("directory.common.exportCsv"), onClick: doExport },
    { label: t("directory.common.clearFilters"), onClick: clearFilters },
  ];

  if (loading)
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center ${A.body}`}
      >
        <div className="text-gray-600">{t("sales.list.loading")}</div>
      </div>
    );

  if (error) return <EmptyState message={t("sales.errors.loadSales")}
    heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;

  // True empty dataset
  if (!sales.length)
    return (
      <EmptyState
        message={t("sales.list.empty")}
        heading={t("directory.common.nothingHere")}
        backLabel={t("directory.common.goBack")}
        showNewButton
        newButtonText={t("sales.list.newSaleCta")}
        newButtonPath={companyRoute(company_id!, "/sales/new")}
      />
    );

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 px-2 ${A.body}`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-5xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`${A.title} text-center flex-1 tracking-tight`}>
          {t("sales.list.title")}
        </h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id!, "/"))}
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* ✅ Shared ListToolbar (2-row layout) */}
      <ListToolbar
        maxWidthClass="max-w-5xl"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("sales.list.search")}
        moreLabel={t("directory.common.more")}
        sortLabel={t("directory.common.sort")}
        fromLabel={t("sales.shared.from")}
        toLabel={t("sales.shared.to")}
        openCalendarLabel={t("sales.shared.openCalendar")}
        sortValue={sortValue}
        sortOptions={sortOptions}
        onSortChange={onToolbarSortChange}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        newItems={newItems}
        moreItems={moreItems}
      />

      {/* Filter produced no results */}
      {sales.length > 0 && totalFiltered === 0 ? (
        <div className="w-full max-w-5xl px-2">
          <div className={`${A.card} p-6 text-center`}>
            <span className="text-gray-600">{t("directory.common.noResults")}</span>
            <button
              type="button"
              onClick={clearFilters}
              className="ml-2 text-[13px] font-normal text-appleblue hover:text-appleblueD"
            >
              {t("directory.common.clearFilters")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3 w-full max-w-5xl px-2">
            {pagedSales.map((sale) => {
              const hasService = sale.lines?.some(
                (l) => l.item_kind === "service",
              );
              const hasPreorder = sale.lines?.some((l) => l.preorder);

              return (
                <div
                  key={sale.id + (hasPreorder ? "_preorder" : "")}
                  className={`${A.card} p-4 cursor-pointer transition-colors hover:bg-gray-50 active:ring-2 active:ring-appleblue/20`}
                  onClick={() =>
                    navigate(companyRoute(company_id!, `/sales/${sale.id}`))
                  }
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      navigate(companyRoute(company_id!, `/sales/${sale.id}`));
                  }}
                  aria-label={t("sales.list.viewSale", { id: sale.id })}
                  style={{
                    minHeight: 120,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-gray-800 font-medium ${A.num}`}>
                      {formatDate(sale.date)}
                    </span>
                    {sale.needs_attention && <span className="text-xs text-amber-800">{t("sales.list.needsAttention")}</span>}
                    <StatusBubble
                      {...displayStatus({
                        ...sale,
                        isPreorder: !!hasPreorder,
                      })}
                    />
                  </div>

                  <div className="flex justify-between items-center gap-3">
                    <div className="min-w-0">
                      <div className="text-gray-700 font-medium truncate">
                        {sale.customer?.name || "—"}
                      </div>
                      {sale.customer?.email && (
                        <div className="text-xs text-gray-400 truncate">
                          {sale.customer.email}
                        </div>
                      )}
                    </div>

                    <div className="text-right">
                      <div
                        className={`text-[15px] font-semibold text-gray-900 ${A.num}`}
                      >
                        {formatAmount(sale.amount)}
                      </div>
                      <div className="flex gap-2 mt-1 justify-end">
                        {hasService && (
                          <span className={servicePill}>
                            {sale.lines?.some(
                              (l) => l.item_kind === "service" && l.product_id,
                            )
                              ? t("sales.common.nonInventory")
                              : t("sales.common.service")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2">
                    <span className="text-xs text-gray-500">{t("sales.list.productsPrefix")} </span>
                    <span className="text-gray-700">
                      {formatProductSummary(sale)}
                    </span>
                  </div>

                  <div className="flex gap-2 items-center mt-2">
                    <span className="text-xs text-gray-500">{t("sales.list.invoicePrefix")}</span>
                    {sale.invoice_id ? (
                      <span className="text-gray-700">
                        {sale.invoice || sale.invoice_id}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block w-full max-w-5xl mx-auto">
            <ListTableShell>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/60">
                    <th
                      className="py-2 px-4 text-left text-xs text-gray-500 select-none cursor-pointer"
                      title={t("sales.list.sortDate")}
                      onClick={() => handleHeaderSort("date")}
                    >
                      {t("sales.common.date")}{" "}
                      {sortKey === "date" && (
                        <span className="text-gray-400">
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>

                    <th
                      className="py-2 px-2 text-left text-xs text-gray-500 select-none cursor-pointer"
                      title={t("sales.list.sortInvoice")}
                      onClick={() => handleHeaderSort("invoice")}
                    >
                      {t("sales.common.invoiceNumber")}{" "}
                      {sortKey === "invoice" && (
                        <span className="text-gray-400">
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>

                    <th className="py-2 px-2 text-left text-xs text-gray-500">
                      {t("sales.common.customer")}
                    </th>
                    <th className="py-2 px-2 text-left text-xs text-gray-500">
                      {t("sales.common.products")}
                    </th>
                    <th className="py-2 px-2 text-right text-xs text-gray-500">
                      {t("sales.common.amount")}
                    </th>
                    <th className="py-2 px-2 text-center text-xs text-gray-500">
                      {t("sales.common.status")}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pagedSales.map((sale) => {
                    const hasService = sale.lines?.some(
                      (l) => l.item_kind === "service",
                    );
                    const hasPreorder = sale.lines?.some((l) => l.preorder);

                    return (
                      <tr
                        key={sale.id + (hasPreorder ? "_preorder" : "")}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer text-gray-700 transition-colors"
                        onClick={() =>
                          navigate(
                            companyRoute(company_id!, `/sales/${sale.id}`),
                          )
                        }
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter")
                            navigate(
                              companyRoute(company_id!, `/sales/${sale.id}`),
                            );
                        }}
                        style={{ outline: "none" }}
                        aria-label={t("sales.list.viewSale", { id: sale.id })}
                      >
                        <td className={`py-2 px-4 ${A.num}`}>
                          {formatDate(sale.date)}
                        </td>

                        <td className="py-2 px-2">
                          {sale.invoice_id ? (
                            <span>{sale.invoice || sale.invoice_id}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        <td className="py-2 px-2">
                          <div className="text-gray-700 font-medium">
                            {sale.customer?.name || "—"}
                          </div>
                          {sale.customer?.email && (
                            <span className="block text-xs text-gray-400">
                              {sale.customer.email}
                            </span>
                          )}
                          <div className="flex gap-2 mt-1">
                            {hasService && (
                              <span className={servicePill}>
                                {sale.lines?.some(
                                  (l) =>
                                    l.item_kind === "service" && l.product_id,
                                )
                                  ? t("sales.common.nonInventory")
                                  : t("sales.common.service")}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-2 px-2">
                          <span className="line-clamp-2">
                            {formatProductSummary(sale)}
                          </span>
                        </td>

                        <td
                          className={`py-2 px-2 text-right font-medium text-gray-900 ${A.num}`}
                        >
                          {formatAmount(sale.amount)}
                        </td>

                        <td className="py-2 px-2 text-center">
                          {sale.needs_attention && <div className="text-xs text-amber-800">{t("sales.list.needsAttention")}</div>}
                          <StatusBubble
                            {...displayStatus({
                              ...sale,
                              isPreorder: !!hasPreorder,
                            })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ListTableShell>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-2 px-1">
              <span className="text-gray-500 select-none">
                {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
              </span>

              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className="px-2 active:scale-100"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t("directory.common.prev")}
                </Button>

                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className="px-2 active:scale-100"
                  disabled={page === totalPages || totalPages === 0}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </div>

          {/* Pagination for mobile */}
          <div className="md:hidden flex justify-between items-center mt-4 w-full max-w-5xl px-2">
            <span className="text-gray-500 select-none">
              {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
            </span>

            <div className="flex gap-2">
              <Button
                variant="tertiary"
                minWidth="0px"
                className="px-2 active:scale-100"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t("directory.common.prev")}
              </Button>

              <Button
                variant="tertiary"
                minWidth="0px"
                className="px-2 active:scale-100"
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
